"use client";

import { TProductComment } from "@/types/productv2";
import VerifiedBadgeCheck from "@/components/common/verified-badge-check";
import Link from "next/link";
import ProfilePicture from "@/components/common/profile-picture";
import { formatDistanceStrict } from "date-fns";
import {
	ClockIcon,
	CornerDownRightIcon,
	Loader2Icon,
	MessageSquareIcon,
	TrashIcon,
} from "lucide-react";
import { useAuth } from "@/providers/auth-provider";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useModal } from "@/providers/modal-provider";
import { ModalType } from "@/types/modal-type";
import toast from "react-hot-toast";
import CustomEditorProvider from "@/providers/custom-editor-provider";
import { useState } from "react";
import cn from "@/lib/cn";
import ProductCommentLikeButton from "./product-comment-like-button";
import CommentMedia from "./comment-media";
import ProductCommentInputField from "./product-comment-input-field";
import { PRODUCTV2_API } from "@/lib/constants";

type ProductCommentCardProps = {
	comment: TProductComment;
	productSlug: string;
	showReplyInput?: boolean;
	toggleReply: (commentId: string) => void;
	selectedCommentId?: string;
	isReply?: boolean;
	onReplySuccess?: () => void;
};

const ProductCommentCard = ({
	comment,
	productSlug,
	showReplyInput,
	selectedCommentId,
	toggleReply,
	isReply,
	onReplySuccess,
}: ProductCommentCardProps) => {
	const { user, isLoggedIn } = useAuth();
	const isAdmin = user?.isAdmin;
	const username = user?.username;
	const isCreator = comment.author.username === username;
	const queryClient = useQueryClient();
	const { openModal } = useModal();
	const [showMoreReplies, setShowMoreReplies] = useState(true);

	const handleDeleteComment = async () => {
		if (!isLoggedIn) {
			openModal(ModalType.Auth);
			return;
		}

		const url = new URL(`${PRODUCTV2_API}/${productSlug}/comments`);

		await fetch(url, {
			method: "DELETE",
			body: JSON.stringify({
				commentSlug: comment.slug,
			}),
		});
	};

	const deleteCommentMutation = useMutation<void, Error>({
		mutationFn: handleDeleteComment,
		onSuccess: () => {
			toast.success("Comment deleted successfully");
			queryClient.invalidateQueries({ queryKey: ["product-comments", productSlug] });
		},
		onError: (error) => toast.error(error.message),
	});

	const replies = comment.replies || [];
	const slicedReplies = showMoreReplies ? replies : replies.slice(0, 1);

	return (
		<div key={comment.id} id={`comment-${comment.slug}`} className="w-full">
			<div className="rounded-lg border bg-brand-primary/[0.5%] p-3">
				<div className="flex items-center justify-between">
					<div className="flex items-center justify-start gap-3">
						<Link href={comment.author.username ? `/${comment.author.username}` : `#`}>
							<ProfilePicture
								profilePictureUrl={comment.author.image}
								alternateText={comment.author.name}
								className={{
									container: "h-10 w-10 rounded-full p-0.5",
									image: "rounded-full object-cover text-gray-600",
									badgeImage: "-bottom-3.5 h-7 w-7",
								}}
								isVerified={comment.author.isVerified}
								isFounder={comment.author.isFounder}
							/>
						</Link>

						<div className="flex flex-col items-start">
							<div className="flex items-center gap-2">
								<Link
									href={comment.author.username ? `/${comment.author.username}` : "#"}
									className="font-bold text-dark-primary md:text-lg"
								>
									{comment.author.name}
								</Link>
								{comment.author.isFounder && comment.author.isVerified && (
									<VerifiedBadgeCheck iconClassName="w-5 h-5" />
								)}
							</div>
							<p className="text-xs text-gray-600">
								{comment.author.jobTitle}
								{comment.author.company ? `, ${comment.author.company}` : ""}
							</p>
						</div>
					</div>

					{(isCreator || isAdmin) && (
						<button
							type="button"
							onClick={() => deleteCommentMutation.mutate()}
							className="rounded-md border p-2 transition-colors duration-200 ease-in-out hover:bg-red-500 hover:bg-opacity-10 disabled:cursor-not-allowed disabled:opacity-60"
							disabled={deleteCommentMutation.isLoading}
						>
							{deleteCommentMutation.isLoading ? (
								<Loader2Icon className="h-4 w-4 animate-spin text-brand-primary" />
							) : (
								<TrashIcon className="h-4 w-4" />
							)}
							<span className="sr-only">Delete Comment</span>
						</button>
					)}
				</div>

				<div className="mt-4">
					<div
						className="whitespace-pre-wrap text-gray-700"
						dangerouslySetInnerHTML={{ __html: comment.legacyContent }}
					/>
				</div>
				<div className="mt-4">
					<CommentMedia items={comment.media} />
				</div>
				<div className="mt-2 flex items-center justify-end gap-4 max-md:flex-wrap">
					<ProductCommentLikeButton
						productSlug={productSlug}
						commentSlug={comment.slug}
						likesCount={comment.likesCount}
						isLiked={comment.isLikedByUser}
					/>

					{!isReply && (
						<button
							type="button"
							onClick={() => toggleReply(comment.id)}
							className={cn(
								"flex items-center justify-start gap-1 rounded-md border px-2 py-1 text-xs",
								comment.id === selectedCommentId
									? "border-brand-primary text-brand-primary"
									: "border-gray-500 text-gray-500"
							)}
						>
							<MessageSquareIcon className="h-4 w-4" />
							<span className="font-medium">Reply</span>
						</button>
					)}

					<div className="flex items-center justify-start gap-1 rounded-md border px-2 py-1 text-xs text-gray-500">
						<ClockIcon className="h-4 w-4" />
						<span>{formatDistanceStrict(new Date(comment.createdAt), new Date())}</span>
					</div>
				</div>
			</div>

			{/* Reply Input */}
			{showReplyInput && comment.id === selectedCommentId && (
				<div className="mt-2 flex items-center justify-start gap-2">
					<CornerDownRightIcon className="h-5 w-5 text-gray-400" />
					<div className="basis-full">
						<CustomEditorProvider defaultValue={{ editorType: "comment" }}>
							<ProductCommentInputField
								productSlug={productSlug}
								parentCommentId={comment.id}
								onSubmit={async () => {
									await queryClient.invalidateQueries({
										queryKey: ["product-comments", productSlug],
									});
									onReplySuccess?.();
								}}
							/>
						</CustomEditorProvider>
					</div>
				</div>
			)}

			{/* Replies */}
			{slicedReplies.length > 0 && (
				<div className="mt-2 space-y-2">
					{slicedReplies.map((reply) => (
						<div key={reply.id} className="flex items-start justify-start gap-2">
							<CornerDownRightIcon className="h-5 w-5 text-gray-400" />
							<ProductCommentCard
								isReply
								productSlug={productSlug}
								comment={reply}
								toggleReply={toggleReply}
								showReplyInput={showReplyInput}
								selectedCommentId={selectedCommentId}
							/>
						</div>
					))}

					{replies.length > 1 && (
						<button
							type="button"
							className="mb-4 ml-7 text-sm font-semibold text-brand-primary"
							onClick={() => setShowMoreReplies((prev) => !prev)}
						>
							{showMoreReplies ? "Hide replies" : "Show more replies"}
						</button>
					)}
				</div>
			)}
		</div>
	);
};

export default ProductCommentCard;
